import { ComponentType } from 'react';
import { Box, List, ListItemButton, Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import {
  ScheduleTask,
  SortedToPlanListBaseProps,
  TaskListCategory
} from '../types';
import { WHITE } from 'constants/colors';
import { TaskListContent } from './TaskListContent';
import { t } from 'i18next';
import { EventResponse } from 'generated/index';
import { PlanningEntity } from 'components/roster-portal/activity-planning/types';
import { Icon } from '@sdb/planning-ui';

type TaskListWrapper = {
  categories: TaskListCategory[];
  uncollapsedCategories: TaskListCategory[];
  scheduleToAssign: ScheduleTask[];
  scheduleWithMultipleDuties: ScheduleTask[];
  getCategoryColor: (category: string) => string;
  handleClick: (category: TaskListCategory) => void;
  activeEntityContainingSelection: string | null;
  onChangeActiveEntity: (entityId: string) => void;
  onChangeSelection: (selectionIds: string[]) => void;
  hasActivityPlanningEditAccess: boolean;
  planningEntityIds: string[];
  onChangeMousePosition: (position: { x: number; y: number }) => void;
  planEventsPerEntity: {
    planningEntityId: string;
    planEvents: EventResponse[];
    planningEntity: PlanningEntity;
  }[];
  onRouteClick?: (date: Date, routeId?: string) => void;
  isTimeline?: boolean;
  amountOfToPlan: number | undefined;
  SortedToPlanListComponent: ComponentType<SortedToPlanListBaseProps>;
};

const TaskListWrapper = ({
  categories,
  uncollapsedCategories,
  scheduleToAssign,
  scheduleWithMultipleDuties,
  getCategoryColor,
  handleClick,
  activeEntityContainingSelection,
  onChangeActiveEntity,
  onChangeSelection,
  hasActivityPlanningEditAccess,
  onChangeMousePosition,
  planEventsPerEntity,
  onRouteClick,
  isTimeline = false,
  amountOfToPlan,
  SortedToPlanListComponent
}) => {
  return (
    <Box display="flex" gap={2} flexDirection="column">
      {categories.map((category: TaskListCategory) => {
        const open = uncollapsedCategories.includes(category);
        const categoryColor = getCategoryColor(
          TaskListCategory[TaskListCategory[category]]
        );

        if (
          category === TaskListCategory.SCHEDULE_TO_ASSIGN &&
          scheduleToAssign.length === 0
        )
          return;

        if (
          category === TaskListCategory.SCHEDULE_WITH_MULTIPLE_DUTIES &&
          scheduleWithMultipleDuties.length === 0
        )
          return;

        return (
          <Box
            key={category}
            sx={{
              bgcolor: WHITE,
              borderRadius: '12px',
              '&:hover': {
                bgcolor: WHITE,
                boxShadow:
                  '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <ListItemButton
              onClick={() => handleClick(category)}
              sx={{
                fontSize: '16px',
                borderRadius: '12px',
                bgcolor: WHITE,
                '&:hover': {
                  bgcolor: WHITE
                }
              }}
            >
              <Box
                borderRadius="100%"
                width={32}
                height={32}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize={16}
                lineHeight={0}
                sx={{
                  bgcolor: categoryColor
                }}
              >
                <div>
                  {category === TaskListCategory.TO_PLAN_APPOINTMENTS &&
                    amountOfToPlan}
                  {category === TaskListCategory.SCHEDULE_TO_ASSIGN &&
                    scheduleToAssign.length}
                  {category ===
                    TaskListCategory.SCHEDULE_WITH_MULTIPLE_DUTIES &&
                    scheduleWithMultipleDuties.length}
                </div>
              </Box>

              <Typography
                flexGrow={1}
                paddingY={2.5}
                paddingLeft={4}
                fontSize={14}
              >
                {t(`taskList.categories.${category}`)}
              </Typography>

              {open ? (
                <Icon type="chevron-up" fontSize="small" color="action" />
              ) : (
                <Icon type="chevron-down" fontSize="small" color="action" />
              )}
            </ListItemButton>

            <Collapse
              in={open}
              timeout="auto"
              unmountOnExit
              sx={{
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px',
                bgcolor: WHITE
              }}
            >
              <List component="div" disablePadding>
                <Box
                  sx={{
                    ppl: 4,
                    borderBottomRightRadius: '12px',
                    borderBottomLeftRadius: '12px',
                    display: 'block',
                    paddingX: '20px',
                    margin: 0,
                    pb: '10px',
                    '&:hover': {
                      bgcolor: WHITE,
                      cursor: 'auto'
                    }
                  }}
                >
                  <TaskListContent
                    category={category}
                    scheduleToAssign={scheduleToAssign}
                    scheduleWithMultipleDuties={scheduleWithMultipleDuties}
                    planEventsPerEntity={planEventsPerEntity}
                    activeEntityContainingSelection={
                      activeEntityContainingSelection
                    }
                    onChangeActiveEntity={onChangeActiveEntity}
                    onChangeSelection={onChangeSelection}
                    hasActivityPlanningEditAccess={
                      hasActivityPlanningEditAccess
                    }
                    onChangeMousePosition={onChangeMousePosition}
                    onRouteClick={onRouteClick}
                    isTimeline={isTimeline}
                    SortedToPlanListComponent={SortedToPlanListComponent}
                  />
                </Box>
              </List>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default TaskListWrapper;
